import {Checkbox, Group, Input, Stack, Text, TextInput, Tooltip} from '@mantine/core';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import CollapsibleCard from 'ui-library/CollapsibleCard';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import ToggleGroup from 'ui-library/ToggleGroup';
import {immutableRemoveAt} from 'util/ImmutableListUtil';
import {Override} from 'util/Types';
import {assertNever} from 'util/Types';
import nameToSqlIdentifier from 'warehouse/nameToSqlIdentifier';

import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../../StepConfigEditorContext';
import {CustomComponentEditorProps} from '../../StepConfigEditorTypes';
import {HelpButtonOrNull} from '../SectionName';

type MatchBy = 'POSITION' | 'NAME' | 'REGEX';

type OutputColumn = NonNullable<
  PipelineStepConfig<PipelineStepType.APPLY_COLUMN_MAPPING, 0, 'draft'>['outputColumns']
>[number];

// A loosened version of OutputColumn for intermediate editing, since the generated type is a strict discriminated union.
interface MutableOutputColumn {
  matchBy: MatchBy;
  columnPosition?: number | null;
  caseSensitive?: boolean | null;
  regEx?: string | null;
  displayName?: string | null;
  systemName?: string | null;
  createEmptyColumnIfNotFound?: boolean | null;
}

type ApplyColumnMappingEditorProps = Override<
  CustomComponentEditorProps,
  {config: PipelineStepConfig<PipelineStepType.APPLY_COLUMN_MAPPING, 0, 'draft'>}
>;

const MATCH_BY_OPTIONS: Array<{id: MatchBy; buttonProps: {text: string}}> = [
  {id: 'POSITION', buttonProps: {text: 'Position'}},
  {id: 'NAME', buttonProps: {text: 'Name'}},
  {id: 'REGEX', buttonProps: {text: 'Regex'}},
];

// This format is also enforced by the step shape (see `apply_column_mapping.yaml`); we additionally check
// the format here, so we can show the user a helpful message. This is the format of a valid SQL identifier,
// except disallowing dollar signs and allowing empty strings (which are treated as null values).
const VALID_SYSTEM_NAME_REGEX = /^$|^[a-z_][a-z0-9_]{0,62}$/;

const SYSTEM_NAME_INFO_TEXT =
  'Set the system name of the output column.\n\n' +
  'Columns are uniquely identified by their system names. If a system name is not set, it will be generated based on the match condition.\n\n' +
  'Setting a system name ensures downstream steps will recognize this column as the same, even if the match condition is edited.\n\n' +
  'The system name must consist of lower-case letters, numbers, and underscores, and cannot start with a number.';

function getDefaultColumn(index: number): OutputColumn {
  return {
    matchBy: 'POSITION',
    columnPosition: index + 1,
    displayName: null,
    systemName: null,
    createEmptyColumnIfNotFound: false,
  };
}

function getColumnTitle(col: MutableOutputColumn): string {
  const matchBy = col?.matchBy ?? 'POSITION';
  switch (matchBy) {
    case 'POSITION': {
      const pos = col?.columnPosition;
      return pos != null ? `Match by position: ${pos}` : 'Match by position';
    }
    case 'NAME': {
      const name = col?.displayName;
      return name ? `Match by name: ${name}` : 'Match by name';
    }
    case 'REGEX': {
      const pattern = col?.regEx;
      return pattern ? `Match by regex: ${pattern}` : 'Match by regex';
    }
    default:
      return assertNever(matchBy);
  }
}

function FieldLabel({
  text,
  required,
  infoText,
}: {
  text: string;
  required?: boolean;
  infoText?: string;
}): JSX.Element {
  return (
    <Group spacing={0} align="center">
      <Input.Label required={required}>{text}</Input.Label>
      {infoText != null && <HelpButtonOrNull infoText={infoText} isCompact={true} />}
    </Group>
  );
}

interface OutputColumnEditorProps {
  column: MutableOutputColumn;
  index: number;
  onChange: (col: MutableOutputColumn) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function OutputColumnEditor({
  column,
  index,
  onChange,
  onRemove,
  disabled,
}: OutputColumnEditorProps): JSX.Element {
  const matchBy = column?.matchBy ?? 'POSITION';

  const title = getColumnTitle(column);

  return (
    <CollapsibleCard
      leftHeader={<Text weight={600}>{title}</Text>}
      rightHeader={
        <Tooltip label="Remove column">
          <span>
            <DeleteIcon icon="trash-can" altText="Remove column" disabled={disabled} onClick={onRemove} />
          </span>
        </Tooltip>
      }
      aria-label={title}
      initiallyExpanded={true}
    >
      <Stack spacing="sm" p="sm">
        <div>
          <FieldLabel text="Match by" required={true} />
          <ToggleGroup<MatchBy>
            options={MATCH_BY_OPTIONS}
            selection={matchBy}
            onChangeSelection={(newMatchBy) => {
              const base: MutableOutputColumn = {
                matchBy: newMatchBy,
                displayName: column?.displayName ?? null,
                systemName: column?.systemName ?? null,
                createEmptyColumnIfNotFound: column?.createEmptyColumnIfNotFound ?? false,
              };
              if (newMatchBy === 'POSITION') {
                onChange({...base, columnPosition: column?.columnPosition ?? index + 1});
              } else if (newMatchBy === 'NAME') {
                onChange({...base, caseSensitive: true, displayName: column?.displayName ?? ''});
              } else {
                onChange({...base, regEx: column?.regEx ?? ''});
              }
            }}
            size={ButtonSize.SMALL}
            isFullWidth={false}
            disabled={disabled}
          />
        </div>

        {matchBy === 'NAME' ? (
          <div>
            <FieldLabel
              text="Column name"
              required={true}
              infoText="Column name to match by. If multiple columns have the same name, the first column will be matched."
            />
            <TextInput
              value={(column?.displayName as string) ?? ''}
              placeholder="Column name to match"
              onChange={(e) => {
                onChange({...column, displayName: e.target.value});
              }}
              disabled={disabled}
            />
          </div>
        ) : (
          <div>
            <FieldLabel text="Column name" infoText="The matched column will be renamed to this name." />
            <TextInput
              value={(column?.displayName as string) ?? ''}
              placeholder="Output column name"
              onChange={(e) => {
                onChange({...column, displayName: e.target.value});
              }}
              disabled={disabled}
            />
          </div>
        )}

        <div>
          <FieldLabel text="System name" infoText={SYSTEM_NAME_INFO_TEXT} />
          <TextInput
            value={(column?.systemName as string) ?? ''}
            placeholder="e.g. my_column_name"
            onChange={(e) => {
              onChange({...column, systemName: e.target.value});
            }}
            disabled={disabled}
          />
          {column?.systemName != null && !VALID_SYSTEM_NAME_REGEX.test(column.systemName) && (
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text="System name must consist of lower-case letters, numbers, and underscores, and cannot start with a number."
            />
          )}
        </div>

        {matchBy === 'POSITION' && (
          <div>
            <FieldLabel text="Column position" required={true} infoText="Column positions start at 1." />
            <IntermediateValueControlledNumericInput
              value={column?.columnPosition ?? null}
              onChange={(e) => {
                onChange({...column, columnPosition: e.target.numericValue});
              }}
              min={1}
              placeholder="Integer"
              isDisabled={disabled}
              validateInteger={true}
            />
          </div>
        )}

        {matchBy === 'NAME' && (
          <Checkbox
            label="Case sensitive"
            checked={column?.caseSensitive ?? true}
            onChange={(e) => {
              onChange({...column, caseSensitive: e.currentTarget.checked});
            }}
            disabled={disabled}
          />
        )}

        {matchBy === 'REGEX' && (
          <div>
            <FieldLabel text="Regex pattern" required={true} />
            <TextInput
              value={(column?.regEx as string) ?? ''}
              placeholder="Regular expression"
              onChange={(e) => {
                onChange({...column, regEx: e.target.value});
              }}
              disabled={disabled}
            />
          </div>
        )}

        <Checkbox
          label="Create an empty column if no matching column is found"
          checked={column?.createEmptyColumnIfNotFound ?? false}
          onChange={(e) => {
            onChange({...column, createEmptyColumnIfNotFound: e.currentTarget.checked});
          }}
          disabled={disabled}
        />
      </Stack>
    </CollapsibleCard>
  );
}

/**
 * Custom editor for PipelineStepType.APPLY_COLUMN_MAPPING.
 *
 * PipelineStepType.APPLY_COLUMN_MAPPING allows users to define how varying input columns are mapped into
 * standardized output columns.
 */
export default function ApplyColumnMappingEditor({
  config,
  onChangeConfig,
  disabled,
}: ApplyColumnMappingEditorProps): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();
  const inputDataFrameInfo = getDataFrameInfoOrEmptyInfo(dataFramesInfos, 'Input dataset');
  const inputColumns = inputDataFrameInfo.dataFrameSpec.columns;
  const hasInputColumns = inputColumns.length > 0;

  const outputColumns = config.outputColumns ?? [];

  return (
    <Stack spacing="sm">
      {outputColumns.map((column, index) => (
        <OutputColumnEditor
          key={`${index}-${column?.systemName}`}
          column={column as MutableOutputColumn}
          index={index}
          onChange={(newCol) => {
            onChangeConfig(
              config,
              ['outputColumns'],
              outputColumns.toSpliced(index, 1, newCol as OutputColumn)
            );
          }}
          onRemove={() => {
            onChangeConfig(config, ['outputColumns'], immutableRemoveAt(outputColumns, index));
          }}
          disabled={disabled}
        />
      ))}

      {!disabled && (
        <Group position="apart" mt={4}>
          <AddItemButton
            onClick={() => {
              onChangeConfig(
                config,
                ['outputColumns'],
                [...outputColumns, getDefaultColumn(outputColumns.length)]
              );
            }}
            label="Add column"
          />
          {hasInputColumns && (
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.SMALL}
              text="Default columns from input"
              onClick={() => {
                const newOutputColumns = inputColumns.map((col, i) => ({
                  matchBy: 'POSITION' as const,
                  columnPosition: i + 1,
                  displayName: col.displayName,
                  systemName: nameToSqlIdentifier(col.displayName, false),
                  createEmptyColumnIfNotFound: false,
                }));

                const newConfig = {
                  ...config,
                  outputColumns: newOutputColumns,
                  keepUnmappedColumns: config.keepUnmappedColumns ?? false,
                };

                onChangeConfig(config, [], newConfig);
              }}
            />
          )}
        </Group>
      )}
    </Stack>
  );
}
